import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Shield, ArrowRight, ArrowLeft, CheckCircle, Brain, 
  Lock, Users, Database, AlertTriangle, Server, Activity,
  ClipboardCheck, Target
} from 'lucide-react';

// Survey questions from CMMC Readiness Self-Assessment
const surveyQuestions = [
  {
    id: 'dod_engagement',
    section: 'DoD Engagement',
    question: 'Do you currently work with the Department of Defense or plan to in the next 12 months?',
    options: [
      { value: 'yes', label: 'Yes', points: 0 },
      { value: 'no', label: 'No', points: 0 }
    ],
    isPrequalifier: true
  },
  // Section 1: Policies & Awareness
  {
    id: 'q1',
    section: 'Policies & Awareness',
    sectionIcon: Users,
    question: 'Do you have written IT security policies?',
    options: [
      { value: 'full', label: 'Yes, fully documented', points: 3 },
      { value: 'partial', label: 'Partially documented', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q2',
    section: 'Policies & Awareness',
    question: 'Do employees receive regular cybersecurity training?',
    options: [
      { value: 'full', label: 'Yes, mandatory and tracked', points: 3 },
      { value: 'partial', label: 'Informal training only', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q3',
    section: 'Policies & Awareness',
    question: 'Are cybersecurity roles and responsibilities clearly defined?',
    options: [
      { value: 'full', label: 'Yes, for all relevant staff', points: 3 },
      { value: 'partial', label: 'Partially', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  // Section 2: Access Controls
  {
    id: 'q4',
    section: 'Access Controls',
    sectionIcon: Lock,
    question: 'Are unique user accounts required for all systems?',
    options: [
      { value: 'yes', label: 'Yes', points: 3 },
      { value: 'partial', label: 'Partially', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q5',
    section: 'Access Controls',
    question: 'Is multi-factor authentication (MFA) implemented where feasible?',
    options: [
      { value: 'full', label: 'Yes, for all critical systems', points: 3 },
      { value: 'partial', label: 'Partial', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q6',
    section: 'Access Controls',
    question: 'Do you have a process to revoke access promptly when employees leave or change roles?',
    options: [
      { value: 'full', label: 'Yes, fully documented', points: 3 },
      { value: 'partial', label: 'Partial / ad-hoc', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  // Section 3: Data Protection
  {
    id: 'q7',
    section: 'Data Protection',
    sectionIcon: Database,
    question: 'Is sensitive data (CUI) encrypted in storage and transit?',
    options: [
      { value: 'full', label: 'Yes, fully encrypted', points: 3 },
      { value: 'partial', label: 'Partial encryption', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q8',
    section: 'Data Protection',
    question: 'Are backups of critical data performed regularly and tested?',
    options: [
      { value: 'full', label: 'Yes, automated and tested', points: 3 },
      { value: 'partial', label: 'Backups exist but not tested', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  // Section 4: Incident Response
  {
    id: 'q9',
    section: 'Incident Response',
    sectionIcon: AlertTriangle,
    question: 'Do you have a formal incident response plan?',
    options: [
      { value: 'full', label: 'Yes, documented and tested', points: 3 },
      { value: 'partial', label: 'Draft / informal', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q10',
    section: 'Incident Response',
    question: 'Are cybersecurity incidents logged, reviewed, and followed up?',
    options: [
      { value: 'full', label: 'Yes, consistently', points: 3 },
      { value: 'partial', label: 'Occasionally', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q11',
    section: 'Incident Response',
    question: 'Have you conducted tabletop exercises or drills?',
    options: [
      { value: 'yes', label: 'Yes', points: 3 },
      { value: 'not_yet', label: 'Not yet', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  // Section 5: Systems & Network
  {
    id: 'q12',
    section: 'Systems & Network',
    sectionIcon: Server,
    question: 'Are operating systems and software updated regularly?',
    options: [
      { value: 'full', label: 'Yes, fully automated/monitored', points: 3 },
      { value: 'partial', label: 'Partially', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q13',
    section: 'Systems & Network',
    question: 'Are antivirus/endpoint protections installed and monitored?',
    options: [
      { value: 'full', label: 'Yes, all endpoints', points: 3 },
      { value: 'partial', label: 'Partial', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q14',
    section: 'Systems & Network',
    question: 'Are firewalls or intrusion detection systems in place?',
    options: [
      { value: 'full', label: 'Yes, for all networks', points: 3 },
      { value: 'partial', label: 'Partial / some systems', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  // Section 6: Continuous Monitoring
  {
    id: 'q15',
    section: 'Continuous Monitoring',
    sectionIcon: Activity,
    question: 'Do you perform periodic internal audits of cybersecurity practices?',
    options: [
      { value: 'full', label: 'Yes, scheduled', points: 3 },
      { value: 'partial', label: 'Occasionally', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q16',
    section: 'Continuous Monitoring',
    question: 'Are risk assessments conducted regularly?',
    options: [
      { value: 'yes', label: 'Yes', points: 3 },
      { value: 'partial', label: 'Partial', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  },
  {
    id: 'q17',
    section: 'Continuous Monitoring',
    question: 'Do you have a remediation process for cybersecurity deficiencies?',
    options: [
      { value: 'full', label: 'Yes, documented and tracked', points: 3 },
      { value: 'partial', label: 'Informal', points: 1 },
      { value: 'no', label: 'No', points: 0 }
    ]
  }
];

const SelfAssessment = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);

  const question = surveyQuestions[currentQuestion];
  const totalQuestions = surveyQuestions.length;
  const progress = ((currentQuestion + 1) / totalQuestions) * 100;

  // Get unique sections for progress indicator
  const sections = [...new Set(surveyQuestions.filter(q => !q.isPrequalifier).map(q => q.section))];
  const currentSection = question.section;
  const currentSectionIndex = sections.indexOf(currentSection);

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [question.id]: value };
    setAnswers(newAnswers);

    // Auto-advance after selection with proper state handling
    const nextQuestionIndex = currentQuestion + 1;
    
    setTimeout(() => {
      if (nextQuestionIndex < totalQuestions) {
        setCurrentQuestion(nextQuestionIndex);
      } else {
        // Survey complete - save answers first
        localStorage.setItem('selfAssessmentAnswers', JSON.stringify(newAnswers));
        
        // Check if user is logged in
        if (isAuthenticated) {
          // Navigate to results page
          navigate('/self-assessment/results');
        } else {
          setIsComplete(true);
          setShowLoginPrompt(true);
        }
      }
    }, 300);
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const handleViewResults = () => {
    navigate('/login', { state: { from: '/self-assessment/results' } });
  };

  const handleCreateAccount = () => {
    navigate('/register', { state: { from: '/self-assessment/results' } });
  };

  // Login prompt modal
  if (showLoginPrompt) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-slate-800/80 backdrop-blur-lg rounded-2xl p-8 border border-violet-500/30 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Assessment Complete!</h2>
          <p className="text-violet-200 mb-8">
            Your CMMC Readiness Self-Assessment is complete. To view your detailed results and personalized recommendations, please sign in or create an account.
          </p>
          
          <div className="space-y-4">
            <button
              onClick={handleViewResults}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl transition-all"
            >
              <Lock className="w-4 h-4" />
              Sign In to View Results
            </button>
            <button
              onClick={handleCreateAccount}
              className="w-full px-6 py-3 border border-violet-500/50 text-violet-200 hover:bg-violet-500/10 font-semibold rounded-xl transition-all"
            >
              Create Free Account
            </button>
          </div>
          
          <p className="text-slate-400 text-sm mt-6">
            Your answers have been saved and will be available after you sign in.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-violet-950 to-slate-900">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-lg border-b border-violet-500/20">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">CMMC Readiness Self-Assessment</h1>
                <p className="text-violet-300 text-sm">Evaluate your cybersecurity posture</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-violet-300 hover:text-white transition-colors text-sm"
            >
              Exit Assessment
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="container mx-auto px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-violet-300 text-sm">Question {currentQuestion + 1} of {totalQuestions}</span>
            <span className="text-violet-300 text-sm">{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-violet-500 to-purple-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Section Indicators */}
          {!question.isPrequalifier && (
            <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-2">
              {sections.map((section, idx) => (
                <div 
                  key={section}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                    idx === currentSectionIndex
                      ? 'bg-violet-500 text-white'
                      : idx < currentSectionIndex
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-slate-700 text-slate-400'
                  }`}
                >
                  {idx < currentSectionIndex && <CheckCircle className="w-3 h-3" />}
                  {section}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Question Card */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-2xl mx-auto">
          {/* Section Header */}
          {question.sectionIcon && (
            <div className="flex items-center gap-2 text-violet-400 mb-4">
              <question.sectionIcon className="w-5 h-5" />
              <span className="text-sm font-medium">{question.section}</span>
            </div>
          )}

          {/* Question */}
          <div className="bg-slate-800/80 backdrop-blur-lg rounded-2xl p-8 border border-violet-500/20 shadow-xl">
            <h2 className="text-2xl font-bold text-white mb-8">{question.question}</h2>

            {/* Answer Options */}
            <div className="space-y-3">
              {question.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                    answers[question.id] === option.value
                      ? 'border-violet-500 bg-violet-500/20 text-white'
                      : 'border-slate-600 hover:border-violet-400 text-slate-200 hover:bg-slate-700/50'
                  }`}
                >
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    answers[question.id] === option.value
                      ? 'border-violet-500 bg-violet-500'
                      : 'border-slate-500'
                  }`}>
                    {answers[question.id] === option.value && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <span className="flex-1">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <button
              onClick={handlePrevious}
              disabled={currentQuestion === 0}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                currentQuestion === 0
                  ? 'text-slate-500 cursor-not-allowed'
                  : 'text-violet-300 hover:text-white hover:bg-violet-500/20'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Previous
            </button>

            {answers[question.id] && currentQuestion < totalQuestions - 1 && (
              <button
                onClick={() => setCurrentQuestion(currentQuestion + 1)}
                className="flex items-center gap-2 px-6 py-2 bg-violet-600 hover:bg-violet-500 text-white rounded-lg transition-colors"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelfAssessment;
